import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { TranslateService } from '@ngx-translate/core';
import { MentionConfig } from 'angular-mentions';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import {
  IActionParamDescriptor,
  IEnumOption,
  ITemplateVariableDescriptor,
  IWorkflowAction,
  IWorkflowActionDescriptor,
  WorkflowActionType,
  WorkflowTriggerType,
} from '../../models/workflows.models';

@Component({
  selector: 'app-workflow-action-row',
  templateUrl: './workflow-action-row.component.html',
  styleUrls: ['./workflow-action-row.component.scss'],
})
export class WorkflowActionRowComponent implements OnInit, OnChanges, OnDestroy {
  @Input() action!: IWorkflowAction;
  /** Descriptors offered in the type dropdown (excludes templateOnly actions). */
  @Input() actionDescriptors: IWorkflowActionDescriptor[] = [];
  /** Every descriptor, used only to RESOLVE this row's own descriptor — a locked
   *  templateOnly action is absent from `actionDescriptors` by design. */
  @Input() descriptorCatalog: IWorkflowActionDescriptor[] = [];
  @Input() enumCache: Record<string, IEnumOption[]> = {};
  /** Fixed by the template: the type cannot be changed and the row cannot be removed. */
  @Input() locked = false;
  /** Selected rule trigger — used to filter trigger-scoped template variables. */
  @Input() triggerType: WorkflowTriggerType | null = null;

  @Output() actionChanged = new EventEmitter<IWorkflowAction>();
  @Output() actionRemoved = new EventEmitter<string>();

  // Host of the template ui-form-field; we reach its inner <textarea> for caret insertion.
  @ViewChild('templateField', { read: ElementRef }) templateFieldRef?: ElementRef<HTMLElement>;

  typeControl = new FormControl<string>('', { nonNullable: true });
  paramControls: Record<string, FormControl> = {};

  selectedDescriptor: IWorkflowActionDescriptor | null = null;

  mentionConfig: MentionConfig = { triggerChar: '@', labelKey: 'name', dropUp: true };
  mentionListOpen = false;
  selectedMentionIds: Record<string, number[]> = {};

  /** email_list controls: committed recipient chips + the free-text input, per param key. */
  emailListValues: Record<string, string[]> = {};
  emailInputControls: Record<string, FormControl<string>> = {};
  readonly separatorKeysCodes = [ENTER, COMMA];
  // Pragmatic address check: restricts local/domain to plausible email characters
  // so markup-ish garbage (e.g. `<b>x</b>@evil.com`) is rejected at input time.
  // Not RFC-exhaustive by design — final format validation is Unipile's at send time.
  private static readonly EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  private readonly destroy$ = new Subject<void>();
  private readonly paramControlsReset$ = new Subject<void>();

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.typeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => this.onTypeChange(type));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['action'] && this.action) {
      this.typeControl.setValue(this.action.type, { emitEvent: false });
      this.updateDescriptor();
      this.syncParamControls();
      this.emitChangeIfStateDrifted();
    }
    // Always after syncParamControls: param controls are created lazily there, so
    // a lock applied earlier would miss the ones that did not exist yet.
    if (changes['action'] || changes['locked']) {
      this.applyLockedState();
    }
  }

  ngOnDestroy(): void {
    this.paramControlsReset$.next();
    this.paramControlsReset$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTypeChange(type: string): void {
    this.typeControl.setValue(type, { emitEvent: false });
    this.updateDescriptor();
    this.paramControlsReset$.next();
    this.paramControls = {};
    this.selectedMentionIds = {};
    this.emailListValues = {};
    this.emailInputControls = {};
    this.syncParamControls();
    this.emitChange();
  }

  onParamChange(key: string, value: any): void {
    if (this.paramControls[key]) {
      this.paramControls[key].setValue(value, { emitEvent: false });
    }
    this.emitChange();
  }

  onMentionSelected(item: { id: number; name: string }, paramKey: string): void {
    if (!this.selectedMentionIds[paramKey]) {
      this.selectedMentionIds[paramKey] = [];
    }
    this.selectedMentionIds[paramKey].push(item.id);
  }

  getMentionables(): { id: number; name: string; initials: string; color: string }[] {
    return (this.enumCache['task.assignee'] ?? []).map((u) => ({
      id: u.value as number,
      name: u.label,
      initials: getUserInitials({ name: u.label }),
      color: getUserColor({ name: u.label }),
    }));
  }

  remove(): void {
    // A locked row has no remove button; this also guards a programmatic call.
    if (this.locked) {
      return;
    }
    this.actionRemoved.emit(this.action.id);
  }

  /** Disable the editing controls of a template-locked row.
   *
   *  `emitEvent: false` matters: enabling/disabling a control emits on
   *  valueChanges, which would re-enter onTypeChange and wipe the params. */
  private applyLockedState(): void {
    if (this.locked) {
      this.typeControl.disable({ emitEvent: false });
    } else {
      this.typeControl.enable({ emitEvent: false });
    }
    for (const control of Object.values(this.paramControls)) {
      if (this.locked) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  addEmail(paramKey: string, event: MatChipInputEvent): void {
    const list = this.emailListValues[paramKey] ?? (this.emailListValues[paramKey] = []);
    let added = false;
    // Split on comma/semicolon/whitespace so a pasted recipient list (e.g. copied
    // from an email client's To field) is tokenised: matChipInputSeparatorKeyCodes
    // only fires on keydown, so a paste arrives here as one unsplit string. Mirrors
    // the backend's `_normalize_recipients`. Invalid tokens are dropped silently.
    for (const token of (event.value || '').split(/[,;\s]+/)) {
      const value = token.trim();
      if (
        value &&
        this.isValidEmail(value) &&
        !list.some((email) => email.toLowerCase() === value.toLowerCase())
      ) {
        list.push(value);
        added = true;
      }
    }
    if (added) {
      this.emitChange();
    }
    event.chipInput?.clear();
    this.emailInputControls[paramKey]?.setValue('');
  }

  removeEmail(paramKey: string, email: string): void {
    const list = this.emailListValues[paramKey];
    const index = list?.indexOf(email) ?? -1;
    if (index >= 0) {
      list.splice(index, 1);
      this.emitChange();
    }
  }

  isValidEmail(email: string): boolean {
    return WorkflowActionRowComponent.EMAIL_REGEX.test(email);
  }

  /** The pickable types, plus this row's own type when it is not among them.
   *  A templateOnly action is absent from `actionDescriptors` by design, so a
   *  locked create_order row would otherwise hold a value matching no option and
   *  render an empty select. Appended rather than merged in place: it must not
   *  become pickable on any other row. */
  get typeOptions(): SelectOption[] {
    const descriptors = [...this.actionDescriptors];
    const ownType = this.typeControl.value;
    if (ownType && !descriptors.some((descriptor) => descriptor.type === ownType)) {
      const own = this.descriptorCatalog.find((descriptor) => descriptor.type === ownType);
      if (own) {
        descriptors.push(own);
      }
    }
    return descriptors.map((d) => ({
      label: this.translate.instant(d.i18nLabel),
      value: d.type,
    }));
  }

  getParamEnumOptions(optionsEndpoint?: string): SelectOption[] {
    if (!optionsEndpoint) return [];
    const match = optionsEndpoint.match(/field=([^&]+)/);
    const fieldKey = match?.[1] ?? '';
    // No placeholder option is injected here: ui-form-field already renders the
    // placeholder from its [placeholder] input. Adding one too produced a duplicate
    // null-valued entry and let the field's own placeholder leak into the list.
    return (this.enumCache[fieldKey] ?? []).map((o) => ({
      label: o.label,
      value: o.value,
    }));
  }

  getParamLabel(i18nLabel: string): string {
    return this.translate.instant(i18nLabel);
  }

  /** Render a variable as its insertable token, e.g. `{{customer.name}}`. */
  variableToken(variableKey: string): string {
    return `{{${variableKey}}}`;
  }

  /** Template variables offered for the current trigger: a variable is shown when
   *  it has no `triggers` scoping or the selected trigger is in its list. */
  visibleVariables(param: IActionParamDescriptor): ITemplateVariableDescriptor[] {
    return (param.variables ?? []).filter(
      (variable) =>
        !variable.triggers?.length ||
        (!!this.triggerType && variable.triggers.includes(this.triggerType))
    );
  }

  /** Insert a `{{token}}` at the caret in the template textarea and write it back
   *  to the param control (plain text — no encode/decode, unlike @mentions). */
  insertVariable(paramKey: string, variableKey: string): void {
    const control = this.paramControls[paramKey];
    if (!control) return;

    const textarea = this.templateFieldRef?.nativeElement.querySelector('textarea') ?? null;
    const token = `{{${variableKey}}}`;
    const current: string = control.value ?? '';
    const start = textarea?.selectionStart ?? current.length;
    const end = textarea?.selectionEnd ?? current.length;
    control.setValue(current.slice(0, start) + token + current.slice(end));

    if (textarea) {
      const caret = start + token.length;
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(caret, caret);
      });
    }
  }

  /** Replace @Name occurrences with @ID for storage. */
  buildContentWithIds(text: string, selectedIds: number[]): string {
    const mentionables = this.getMentionables();
    const byLength = [...mentionables].sort((a, b) => b.name.length - a.name.length);
    const mentions: { start: number; end: number; id: number }[] = [];

    let i = 0;
    while (i < text.length) {
      if (text[i] === '@') {
        let matched = false;
        for (const m of byLength) {
          const rest = text.slice(i + 1, i + 1 + m.name.length);
          if (rest === m.name) {
            mentions.push({ start: i, end: i + 1 + m.name.length, id: m.id });
            i = i + 1 + m.name.length;
            matched = true;
            break;
          }
        }
        if (!matched) i++;
      } else {
        i++;
      }
    }

    const ids = mentions.map((m, idx) => selectedIds[idx] ?? m.id);
    let result = '';
    let last = 0;
    mentions.forEach((m, idx) => {
      result += text.slice(last, m.start) + '@' + ids[idx];
      last = m.end;
    });
    result += text.slice(last);
    return result;
  }

  /** Replace @ID references with @Name for display when loading a saved action. */
  decodeContentFromIds(text: string): string {
    const users = this.enumCache['task.assignee'] ?? [];
    return text.replace(/@(\d+)/g, (_match, rawId) => {
      const userId = parseInt(rawId, 10);
      const user = users.find((u) => u.value === userId);
      return user ? `@${user.label}` : `@${rawId}`;
    });
  }

  /** Resolve the descriptor for the selected type.
   *
   *  Falls back to `descriptorCatalog` because `actionDescriptors` is only the
   *  *pickable* set: a templateOnly action (e.g. create_order on a locked row) is
   *  deliberately absent from it, and without this fallback the row would find no
   *  descriptor and render nothing. */
  private updateDescriptor(): void {
    const type = this.typeControl.value;
    this.selectedDescriptor =
      this.actionDescriptors.find((d) => d.type === type) ??
      this.descriptorCatalog.find((d) => d.type === type) ??
      null;
  }

  private syncParamControls(): void {
    if (!this.selectedDescriptor) return;
    for (const param of this.selectedDescriptor.params) {
      if (param.controlType === 'email_list') {
        if (!(param.key in this.emailListValues)) {
          const existing = this.action.params?.[param.key];
          this.emailListValues[param.key] = Array.isArray(existing)
            ? existing.filter((email): email is string => typeof email === 'string')
            : [];
          this.emailInputControls[param.key] = new FormControl('', { nonNullable: true });
        }
        continue;
      }
      if (!this.paramControls[param.key]) {
        const existingValue = this.action.params?.[param.key];
        let initialValue: any;
        if (param.controlType === 'mention_textarea' && existingValue) {
          initialValue = this.decodeContentFromIds(existingValue);
        } else if (existingValue !== undefined) {
          initialValue = existingValue;
        } else if (param.controlType === 'order_field_modifications') {
          // Composite object control — hydrate from an object, not a string.
          // While empty it is dropped by emitChange (see isEmptyParamValue), so
          // an untouched picker never persists a meaningless `modifications: {}`.
          initialValue = {};
        } else {
          initialValue = param.controlType === 'select' ? null : (param.defaultValue ?? '');
        }
        const control = new FormControl(initialValue);
        control.valueChanges
          .pipe(takeUntil(this.destroy$), takeUntil(this.paramControlsReset$))
          .subscribe((value) => this.onParamChange(param.key, value));
        this.paramControls[param.key] = control;
      }
    }
  }

  /** Whether a param value carries no meaningful selection and should be
   *  dropped from the emitted params. Extends the ''/null drop to an empty
   *  object (the modifications composite with nothing picked), so an untouched
   *  control never persists a meaningless key like `modifications: {}`. */
  private isEmptyParamValue(value: any): boolean {
    if (value === '' || value == null) return true;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value).length === 0;
    }
    return false;
  }

  private emitChange(): void {
    const params: Record<string, any> = {};
    for (const [key, control] of Object.entries(this.paramControls)) {
      const value = control.value;
      if (this.isEmptyParamValue(value)) continue;

      const paramDescriptor = this.selectedDescriptor?.params.find((p) => p.key === key);
      if (paramDescriptor?.controlType === 'mention_textarea') {
        params[key] = this.buildContentWithIds(value, this.selectedMentionIds[key] ?? []);
      } else {
        params[key] = value;
      }
    }
    // email_list params live outside paramControls; emit the recipient array.
    // Omit the key when empty so a `required` email_list reads as missing to the
    // builder's canSave param-completeness check (WorkflowBuilderStepComponent).
    for (const [key, emails] of Object.entries(this.emailListValues)) {
      if (emails.length) params[key] = [...emails];
    }
    this.actionChanged.emit({
      ...this.action,
      type: this.typeControl.value as WorkflowActionType,
      params,
    });
  }

  private emitChangeIfStateDrifted(): void {
    const nextParams: Record<string, any> = {};
    for (const [key, control] of Object.entries(this.paramControls)) {
      if (!this.isEmptyParamValue(control.value)) {
        nextParams[key] = control.value;
      }
    }
    for (const [key, emails] of Object.entries(this.emailListValues)) {
      if (emails.length) nextParams[key] = emails;
    }

    const currentParams = this.action.params ?? {};
    const currentParamKeys = Object.keys(currentParams);
    const nextParamKeys = Object.keys(nextParams);
    const hasParamDrift =
      currentParamKeys.length !== nextParamKeys.length
      || nextParamKeys.some((key) => !this.paramValuesEqual(currentParams[key], nextParams[key]));

    if (this.action.type !== this.typeControl.value || hasParamDrift) {
      this.emitChange();
    }
  }

  private paramValuesEqual(a: any, b: any): boolean {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.length === b.length && a.every((value, index) => value === b[index]);
    }
    return a === b;
  }
}
